import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Collapse,
  InputGroupAddon,
} from 'reactstrap';
import { Form } from '@availity/form';
import { Select, Input, Switch } from '../../lib/form-field';
import '@availity/yup';
import formValidation from './formValidation';

import * as acquisitionOrdersActions from '../../store/acquisitionOrders/actions';
import * as messageTemplatesActions from '../../store/messageTemplates/actions';
import * as monetizationOrdersActions from '../../store/monetizationOrders/actions';
import * as leadFormsActions from '../../store/leadForms/actions';
import { useSelector, useDispatch } from 'react-redux';

import QueryBuilder from '../../components/Common/QueryBuilder';

import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, convertFromRaw } from 'draft-js';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import { SubmitButton } from '../Common/Modal/modalActionButtons';
import { components } from 'react-select';
import { capitalize } from '../../helpers/sharedFunction';
import sectors from '../../config/sectors';
import customerWebServices from '../../config/customerWebServices';
import toastr from 'toastr';
import { useTranslation } from 'react-i18next';
import { actualCountry } from '../../selectors/country';
import { selectAiAgents } from '../../selectors/aiAgent';
import { aiRoutingModes } from '../../config/aiAgents';
import { hasCallCenter } from '../../config/callCenterHours';
import { writeInCountryLanguage } from '../../helpers/countryLanguage';
import * as aiAgentsActions from '../../store/aiAgents/actions';

const MAX_SELECTED_MONETIZATION_ORDERS = 30;
const { Option } = components;

const MessageTemplateSelectOption = (props) => (
  <Option {...props}>
    {props.data.type
      ? `${capitalize(props.data.type)}${
          props.data.linkType ? ' - ' + capitalize(props.data.linkType) : ''
        } - ${props.data.label}`
      : props.data.label}
  </Option>
);

const FormAcquisitionOrder = (props) => {
  const editingOrder = props.acquisitionOrder || {};

  const emptyEditor = EditorState.createEmpty();

  const monetizationOrders = useSelector(
    (state) => state.MonetizationOrder.monetizationOrders,
  );
  const leadForms = useSelector((state) => state.LeadForm.leadForms);
  const leadFields = useSelector((state) => state.LeadField.leadFields);
  const country = useSelector(actualCountry);
  const { t } = useTranslation();
  const aiAgents = useSelector(selectAiAgents());
  const [active, setActive] = useState(editingOrder.active || false);
  const [qualified, setQualified] = useState(editingOrder.qualified || 'no');

  const messageTemplates = props.messageTemplates.map(
    ({ id, name, templateType, linkType }) => ({
      value: id,
      label: name,
      type: templateType,
      linkType: linkType,
    }),
  );

  const initialQuery = editingOrder.query || null;
  const initialSqlQuery = editingOrder.sqlQuery || null;
  const initialJsonLogicQuery = editingOrder.jsonLogicQuery || null;
  const initialNote = editingOrder.note
    ? EditorState.createWithContent(
        convertFromRaw(JSON.parse(editingOrder.note)),
      )
    : emptyEditor;
  const initialSuggestion = editingOrder.suggestion
    ? EditorState.createWithContent(
        convertFromRaw(JSON.parse(editingOrder.suggestion)),
      )
    : emptyEditor;

  const [selectedQuery, setSelectedQuery] = useState(initialQuery);
  const [selectedSqlQuery, setSelectedSqlQuery] = useState(initialSqlQuery);
  const [selectedJsonLogicQuery, setSelectedJsonLogicQuery] = useState(
    initialJsonLogicQuery,
  );
  const [selectedNote, setSelectedNote] = useState(initialNote);
  const [selectedSuggestion, setSelectedSuggestion] =
    useState(initialSuggestion);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(messageTemplatesActions.syncMessageTemplates());
    dispatch(monetizationOrdersActions.syncMonetizationOrders());
    dispatch(leadFormsActions.syncLeadForms());
    if (country?.aiModule) dispatch(aiAgentsActions.syncAiAgents());
  }, []);

  const handleSubmit = (values) => {
    if (qualified === 'yes' && !selectedQuery)
      return toastr.error('Check the fields!', 'Error');
    const { messageTemplateId, negativeMessageTemplateId } = values;
    if (
      messageTemplateId !== 'none' &&
      messageTemplateId === negativeMessageTemplateId
    )
      return toastr.error(
        'Select different message template, please!',
        'Error',
      );

    const negativeReasonsArray = !values.negativeReasons
      ? []
      : Array.isArray(values.negativeReasons)
        ? values.negativeReasons
        : values.negativeReasons.split(',');

    const jsonLogicQuery = selectedJsonLogicQuery?.logic
      ? selectedJsonLogicQuery
      : '';

    const acquisitionOrder = {
      ...values,
      query: selectedQuery || '',
      sqlQuery: selectedSqlQuery || '',
      jsonLogicQuery,
      negativeReasons: negativeReasonsArray,
      note:
        selectedNote === emptyEditor
          ? null
          : JSON.stringify(convertToRaw(selectedNote.getCurrentContent())),
      suggestion:
        selectedSuggestion === emptyEditor
          ? null
          : JSON.stringify(
              convertToRaw(selectedSuggestion.getCurrentContent()),
            ),
      active,
    };

    // Formik stores '' for an empty number input and the saga writes the object
    // straight to Firestore, so leaving it would persist '' on every order —
    // including countries with no AI module, where this field never renders.
    // The engine's default is "absent means one offer": '' is not absent, and
    // Math.min(n, '') is 0, which would silently propose nothing.
    if (acquisitionOrder.aiMaxOffersPerClosure === '')
      delete acquisitionOrder.aiMaxOffersPerClosure;

    dispatch(
      props.create
        ? acquisitionOrdersActions.createAcquisitionOrder(acquisitionOrder)
        : acquisitionOrdersActions.updateAcquisitionOrder(
            {
              id: editingOrder.id,
              ...acquisitionOrder,
            },
            editingOrder.campaignIds,
          ),
    );
    props.closeModal();
  };

  const handleQueryTree = (jsonTree, sqlQuery, jsonLogicQuery) => {
    setSelectedQuery(jsonTree);
    setSelectedSqlQuery(sqlQuery);
    setSelectedJsonLogicQuery(jsonLogicQuery);
  };

  return (
    <Card>
      <CardBody>
        <Form
          initialValues={{
            qualified: 'no',
            aiHandoffMessage: '',
            aiMaxOffersPerClosure: '',
            aiRouting: {
              enabled: false,
              agentId: '',
              mode: 'autonomous',
              splitPercentage: 0,
              onlyWhenCallCenterClosed: false,
            },
            ...editingOrder,
            negativeReasons: Array.isArray(editingOrder.negativeReasons)
              ? editingOrder.negativeReasons.join(',')
              : editingOrder.negativeReasons,
          }}
          validationSchema={formValidation}
          onSubmit={handleSubmit}>
          {({ isValid, values }) => (
            <>
              <Row>
                <Col md="3">
                  <Input id="name" name="name" />
                </Col>
                <Col md="3">
                  <Select
                    name="customerId"
                    id="customerId"
                    label="Customer"
                    isMulti={false}
                    options={
                      Array.isArray(props.customers)
                        ? props.customers.map(({ id, name }) => ({
                            label: name,
                            value: id,
                          }))
                        : []
                    }
                  />
                </Col>
                <Col md="6">
                  <Select
                    name="campaignIds"
                    id="campaignIds"
                    label="Campaigns"
                    isMulti
                    options={props.campaigns}
                  />
                </Col>
              </Row>
              <Row>
                <Col md="2">
                  <Select
                    name="qualified"
                    id="qualified"
                    label="Qualified?"
                    onChange={setQualified}
                    value={qualified}
                    options={[
                      {
                        label: 'Yes',
                        value: 'yes',
                      },
                      {
                        label: 'No',
                        value: 'no',
                      },
                    ]}
                  />
                </Col>
                <Col md="3">
                  <Select
                    name="messageTemplateId"
                    id="messageTemplateId"
                    label="Message on entry"
                    isMulti={false}
                    options={[
                      {
                        value: 'none',
                        label: 'None',
                        type: null,
                        linkType: null,
                      },
                      ...messageTemplates,
                    ]}
                    components={{ Option: MessageTemplateSelectOption }}
                  />
                </Col>
                <Col md="2">
                  <Select
                    name="leadFormId"
                    id="leadFormId"
                    label="Lead Form"
                    options={[
                      { value: 'none', label: 'None' },
                      ...leadForms.map(({ id, name }) => ({
                        label: name,
                        value: id,
                      })),
                    ]}
                  />
                </Col>
                <Col md="2">
                  <Select
                    name="sector"
                    id="sector"
                    label="Sector"
                    options={sectors}
                    isDisabled={!props.create}
                  />
                </Col>
                <Col md="1">
                  <Input
                    id="priority"
                    name="priority"
                    type="number"
                    step="1"
                    min="1"
                    max="10"
                  />
                  <p className="font-size-12 text-muted">
                    1 = highest priority
                  </p>
                </Col>
                <Col md="2">
                  <Switch
                    name="active"
                    label="Active?"
                    defaultChecked={active}
                    onClick={() => setActive(!active)}
                  />
                </Col>
              </Row>
              <Collapse isOpen={qualified === 'yes'}>
                <Row className="mt-3 mb-2">
                  <Col xs="auto" className="d-flex align-items-center">
                    <h5 className="m-0">Call Center</h5>
                  </Col>
                  <Col>
                    <hr />
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <QueryBuilder
                        label="Conditional logic for call-center sending"
                        debug={false}
                        fields={leadFields}
                        onChange={handleQueryTree}
                        initialQuery={initialQuery}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="3">
                    <Input
                      id="costNegativeClosure"
                      name="costNegativeClosure"
                      label="Cost per negative closure"
                      min={0}
                      type="number"
                      step="0.01">
                      <InputGroupAddon addonType="append">€</InputGroupAddon>
                    </Input>
                  </Col>
                  <Col md="2">
                    <Input
                      id="maxCallAttempts"
                      name="maxCallAttempts"
                      label="Max call attempts"
                      min={2}
                      type="number"
                      step="1"
                    />
                  </Col>
                  <Col md="2">
                    <Input
                      id="phoneExchangeRoute"
                      name="phoneExchangeRoute"
                      label="Phone exchange route"
                    />
                  </Col>
                  <Col md="2">
                    <Input
                      id="phoneExchangeQueue"
                      name="phoneExchangeQueue"
                      label="Phone exchange queue"
                    />
                  </Col>
                  <Col md="3">
                    <Input
                      id="sendMessageAfterRecall"
                      name="sendMessageAfterRecall"
                      label="Send Message after recall n."
                      defaultValue={0}
                      min={0}
                      max={parseInt(values.maxCallAttempts) - 1}
                      type="number"
                      step="1"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <Input
                      id="negativeReasons"
                      name="negativeReasons"
                      type="textarea"
                      label="Custom Negative Reasons"
                    />
                    <p className="font-size-12 text-muted">
                      Insert comma separated strings
                    </p>
                  </Col>
                  <Col md="2">
                    <Input
                      id="callStopwatch"
                      name="callStopwatch"
                      label="Timer (in minutes)"
                      defaultValue={0}
                      min={0}
                      max={10}
                      type="number"
                      step="0.1"
                    />
                  </Col>
                  <Col md="3">
                    <Collapse
                      isOpen={parseInt(values.sendMessageAfterRecall) > 0}>
                      <Select
                        name="recallMessageTemplateId"
                        id="recallMessageTemplateId"
                        label="Recall Message Template"
                        options={messageTemplates}
                        components={{ Option: MessageTemplateSelectOption }}
                      />
                    </Collapse>
                  </Col>
                </Row>
              </Collapse>
              <Row className="mt-3 mb-2">
                <Col xs="auto" className="d-flex align-items-center">
                  <h5 className="m-0">Monetization Orders</h5>
                </Col>
                <Col>
                  <hr />
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <Select
                    name="monetizationOrdersAllowed"
                    id="monetizationOrdersAllowed"
                    label="Monetization Orders allowed"
                    isMulti
                    maxLength={MAX_SELECTED_MONETIZATION_ORDERS}
                    isSearchable
                    options={monetizationOrders.map((mo) => ({
                      value: mo.id,
                      label: mo.name,
                    }))}
                  />
                </Col>
              </Row>
              <Row className="mt-3 mb-2">
                <Col xs="auto" className="d-flex align-items-center">
                  <h5 className="m-0">Messages</h5>
                </Col>
                <Col>
                  <hr />
                </Col>
              </Row>
              <Row>
                <Col md="3">
                  <Collapse isOpen={qualified === 'yes'}>
                    <Select
                      name="negativeWebService"
                      id="negativeWebService"
                      label="Web Service on negative"
                      options={[
                        { value: 'none', label: 'None' },
                        ...customerWebServices,
                      ]}
                    />
                    <p className="font-size-12 text-muted">
                      All negative lead statuses including 'expired' are
                      monitored.
                    </p>
                  </Collapse>
                </Col>
                <Col md="3">
                  <Select
                    name="negativeMessageTemplateId"
                    id="negativeMessageTemplateId"
                    label="Message on negative"
                    isMulti={false}
                    options={[
                      {
                        value: 'none',
                        label: 'None',
                        type: null,
                        linkType: null,
                      },
                      ...messageTemplates,
                    ]}
                    components={{ Option: MessageTemplateSelectOption }}
                  />
                </Col>
                <Col md="2">
                  <Collapse
                    isOpen={
                      values.negativeMessageTemplateId &&
                      values.negativeMessageTemplateId !== 'none'
                    }>
                    <Input
                      id="negativeMessageSendDelay"
                      name="negativeMessageSendDelay"
                      label="Negative Send Delay"
                      min={1} //min 1 delay day because the person must be created before sending a message
                      type="number"
                      step="1"
                    />
                    <p className="font-size-12 text-muted">Value in days</p>
                  </Collapse>
                </Col>
                <Col md="3">
                  <Select
                    name="expiredMessageTemplateId"
                    id="expiredMessageTemplateId"
                    label="Message on expired"
                    isMulti={false}
                    options={[
                      {
                        value: 'none',
                        label: 'None',
                        type: null,
                        linkType: null,
                      },
                      ...messageTemplates,
                    ]}
                    components={{ Option: MessageTemplateSelectOption }}
                  />
                </Col>
              </Row>
              <Row className="mt-3 mb-2">
                <Col xs="auto" className="d-flex align-items-center">
                  <h5 className="m-0">Notes</h5>
                </Col>
                <Col>
                  <hr />
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label htmlFor="note">Note</Label>
                    <Editor
                      toolbarClassName="toolbarClassName"
                      wrapperClassName="wrapperClassName"
                      editorClassName="editorClassName"
                      placeholder="Place Your Content Here..."
                      editorState={selectedNote}
                      onEditorStateChange={(editorState) =>
                        setSelectedNote(editorState)
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label htmlFor="note">Suggestion</Label>
                    <Editor
                      toolbarClassName="toolbarClassName"
                      wrapperClassName="wrapperClassName"
                      editorClassName="editorClassName"
                      placeholder="Place Your Content Here..."
                      editorState={selectedSuggestion}
                      onEditorStateChange={(editorState) =>
                        setSelectedSuggestion(editorState)
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>
              {country?.aiModule && (
                <>
                  <hr />
                  <h5 className="font-size-14 mb-3">{t('AI routing')}</h5>
                  <Row>
                    <Col md="4">
                      <Switch
                        name="aiRouting.enabled"
                        label={t('Enable AI channel?')}
                        defaultChecked={values.aiRouting?.enabled}
                      />
                    </Col>
                  </Row>
                  <Collapse isOpen={!!values.aiRouting?.enabled}>
                    <Row>
                      <Col md="5">
                        <Select
                          name="aiRouting.agentId"
                          id="aiRouting.agentId"
                          label="AI Agent"
                          isMulti={false}
                          isSearchable
                          options={aiAgents
                            .filter(({ active }) => active !== false)
                            .map(({ id, name, channel }) => ({
                              label: `${name} (${channel})`,
                              value: id,
                            }))}
                        />
                      </Col>
                      <Col md="4">
                        <Select
                          name="aiRouting.mode"
                          id="aiRouting.mode"
                          label="Mode"
                          isMulti={false}
                          options={aiRoutingModes}
                          subText={t(
                            'Autonomous: the agent takes the conversation to its outcome by itself. Pre-qualify: it only enriches the lead, then hands it to the call center queue.',
                          )}
                        />
                      </Col>
                      <Col md="3">
                        <Input
                          type="number"
                          name="aiRouting.splitPercentage"
                          label="Split percentage"
                          subText={t(
                            'Share of leads routed to the agent. The rest goes to the call center as usual, so the two can be compared on the same reports.',
                          )}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <Switch
                          name="aiRouting.onlyWhenCallCenterClosed"
                          label={t('Only when the call center is closed?')}
                          defaultChecked={
                            values.aiRouting?.onlyWhenCallCenterClosed
                          }
                          subText={t(
                            'The split percentage then applies only to the leads arriving outside the call center hours. While the call center is open, every lead goes to the operators.',
                          )}
                        />
                        {/* Without hours the routing finds the call center
                            neither open nor closed, and sends nothing to the
                            agent: better said here than discovered later. */}
                        {values.aiRouting?.onlyWhenCallCenterClosed &&
                          !hasCallCenter(country) && (
                            <small className="font-size-12 text-danger d-block">
                              {t(
                                'This country has no call center hours configured: with this option on, no lead goes to the agent.',
                              )}
                            </small>
                          )}
                      </Col>
                    </Row>
                    <Row>
                      <Col md="4">
                        <Input
                          type="number"
                          name="aiMaxOffersPerClosure"
                          label="How many offers at most, together"
                          min={1}
                          step="1"
                          subText={t(
                            'How many offers at most this agent may have accepted in one conversation. Left empty it is one, which is what it does today: it proposes an offer and closes. Raise it and, after a yes, it also names the next offer the lead qualifies for and may close both together. It can never be more than the offers this acquisition order carries, and a no stops it: the number caps how many can be accepted, not how many times the agent may ask.',
                          )}
                        />
                      </Col>
                      <Col md="8">
                        <Input
                          type="textarea"
                          name="aiHandoffMessage"
                          label="What happens next, when the work passes to a person"
                          required={
                            values.aiRouting?.enabled === true &&
                            !!values.aiRouting?.agentId
                          }
                          rows="3"
                          subText={`${t(
                            'The agent hands the conversation to a person when it proposes several offers together, when it has finished collecting the data in pre-qualification, or when the conversation has run too long. The same text covers every case, so do not name a product or a customer. Nothing is settled yet, so do not promise what the customer will do: write that somebody will get in touch. Do not treat the conversation as closed and do not invite her to write again, because in some cases the agent still answers and in others it does not. Sent word for word, so write it as you would to the lead.',
                          )} ${writeInCountryLanguage(
                            t,
                            country?.languageTag,
                          )}`}
                        />
                      </Col>
                    </Row>
                  </Collapse>
                </>
              )}
              <div className="modal-footer">
                <SubmitButton
                  disabled={!isValid || props.loading}
                  create={props.create}
                />
              </div>
            </>
          )}
        </Form>
      </CardBody>
    </Card>
  );
};

export default FormAcquisitionOrder;
